import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { RotateCcw, Download } from "lucide-react";

const ALL = "__all";
const COLS = [
  ["active_advertisers", "Active advertisers"], ["positions_used", "Positions used"], ["positions_available", "Positions available"],
  ["monthly_revenue", "Monthly revenue", true], ["renewals_due", "Renewals due"], ["expired", "Expired"],
  ["open_leads", "Open leads"], ["pipeline_value", "Pipeline value", true], ["won_deals", "Won deals"],
];
const NO_DRILL = new Set(["positions_available"]);
const LINK = { placement: (id) => `/businesses/${id}?tab=placements`, contract: (id) => `/contracts/${id}`, lead: (id) => `/leads/${id}` };

function Pick({ value, onChange, label, items, testid }) {
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="h-8 w-48" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value={ALL}>{label}</SelectItem>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Drill({ drill, onClose }) {
  const navigate = useNavigate();
  return (
    <Dialog open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="zr-drill">
        <DialogHeader><DialogTitle className="font-display">{drill?.title}</DialogTitle><DialogDescription>{drill?.items ? `${drill.items.length} record(s)` : "Loading…"}</DialogDescription></DialogHeader>
        <ul className="divide-y divide-slate-100">
          {(drill?.items || []).map((it, i) => (
            <li key={`${it.id}-${i}`}><button className="w-full text-left py-2 hover:bg-slate-50 px-1" onClick={() => navigate(LINK[it.type](it.id))} data-testid={`zr-drill-item-${it.id}`}>
              <p className="text-sm font-semibold text-slate-800">{it.label || "—"}</p>
              <p className="text-xs text-slate-400">{it.sub}{it.monthly != null ? ` · ${fmtMoney(it.monthly)}/mo` : ""}{it.value ? ` · ${fmtMoney(it.value)}` : ""}</p>
            </button></li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

export default function ZoneReports() {
  const { user } = useAuth();
  const isRep = user?.role === "sales_rep";
  const [f, setF] = useState({ market_id: "", zone_id: "", assigned_to: "", date_from: "", date_to: "" });
  const [opts, setOpts] = useState({ zones: [], users: [] });
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);
  useEffect(() => {
    api.get("/mz/zone-options").then((r) => setOpts((o) => ({ ...o, zones: r.data.items })));
    if (!isRep) api.get("/users").then((r) => setOpts((o) => ({ ...o, users: r.data.items.filter((u) => u.invite_status !== "Cancelled") }))).catch(() => {});
  }, [isRep]);
  const params = useCallback(() => Object.fromEntries(Object.entries(f).filter(([, v]) => v)), [f]);
  useEffect(() => { setData(null); api.get("/zone-reports", { params: params() }).then((r) => setData(r.data)); }, [params]);
  const markets = [...new Map(opts.zones.map((z) => [z.market_id, z.market])).entries()];
  const zones = opts.zones.filter((z) => !f.market_id || z.market_id === f.market_id).map((z) => [z.id, `${z.market} › ${z.name}`]);
  const open = async (r, k, label) => {
    if (NO_DRILL.has(k) || !r[k]) return;
    setDrill({ title: `${r.market} › ${r.zone} — ${label}` });
    const { data: d } = await api.get("/zone-reports/drill", { params: { ...params(), key: r.key, metric: k } });
    setDrill({ title: `${r.market} › ${r.zone} — ${label}`, items: d.items });
  };
  const fmt = (r, k, money) => (r[k] == null ? "—" : money ? fmtMoney(r[k]) : r[k]);
  const csv = () => {
    const head = ["Market", "Zone", ...COLS.map((c) => c[1])];
    const lines = [head, ...data.rows.map((r) => [r.market, r.zone, ...COLS.map(([k]) => r[k] ?? "")])].map((l) => l.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(","));
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" })); a.download = "zone-report.csv"; a.click();
  };

  return (
    <div className="space-y-3" data-testid="zone-reports">
      <div className="flex flex-wrap items-center gap-2">
        <Pick value={f.market_id} onChange={(v) => setF({ ...f, market_id: v, zone_id: "" })} label="All markets" items={markets} testid="zr-f-market" />
        <Pick value={f.zone_id} onChange={(v) => setF({ ...f, zone_id: v })} label="All zones" items={zones} testid="zr-f-zone" />
        {!isRep && <Pick value={f.assigned_to} onChange={(v) => setF({ ...f, assigned_to: v })} label="All salespeople" items={opts.users.map((u) => [u.id, u.name])} testid="zr-f-salesperson" />}
        <Input type="date" className="h-8 w-40" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="zr-f-from" />
        <span className="text-xs text-slate-400">to</span>
        <Input type="date" className="h-8 w-40" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="zr-f-to" />
        <Button size="sm" variant="outline" onClick={() => setF({ market_id: "", zone_id: "", assigned_to: "", date_from: "", date_to: "" })} data-testid="zr-reset"><RotateCcw className="h-4 w-4" />Reset</Button>
        <Button size="sm" variant="outline" className="ml-auto" onClick={csv} disabled={!data} data-testid="zr-export"><Download className="h-4 w-4" />CSV</Button>
      </div>
      <p className="text-[11px] text-slate-400">Advertisers, positions, revenue, renewals and expired are as of today. The date range applies to open leads (created) and won deals (won date). Click a number to see the records.</p>
      <div className="cc-card overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead><tr className="border-b bg-slate-50/80 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2.5">Market › Zone</th>{COLS.map(([k, l]) => <th key={k} className="px-3 py-2.5 text-right whitespace-nowrap">{l}</th>)}
          </tr></thead>
          <tbody>
            {!data ? <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-400">Loading…</td></tr>
              : data.rows.length === 0 ? <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-400">No zones match these filters.</td></tr>
              : data.rows.map((r) => (
                <tr key={r.key} className="border-b border-slate-100" data-testid={`zr-row-${r.key}`}>
                  <td className="px-3 py-2"><span className="text-slate-400">{r.market} ›</span> <span className={`font-medium ${r.zone_id ? "" : "italic text-slate-500"}`}>{r.zone}</span></td>
                  {COLS.map(([k, , money]) => (
                    <td key={k} className="px-3 py-2 text-right tnum">
                      <button onClick={() => open(r, k, COLS.find((c) => c[0] === k)[1])} disabled={NO_DRILL.has(k) || !r[k]} data-testid={`zr-cell-${r.key}-${k}`}
                        className={(k === "positions_available" && r[k] != null && r[k] <= 0) || (k === "expired" && r[k]) ? "text-red-600 font-semibold" : !NO_DRILL.has(k) && r[k] ? "text-[#0094A8] hover:underline" : "text-slate-600"}>{fmt(r, k, money)}</button>
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
          {data && data.rows.length > 0 && (
            <tfoot><tr className="bg-slate-50 font-semibold" data-testid="zr-totals">
              <td className="px-3 py-2.5">Total ({data.rows.filter((r) => r.zone_id).length} zones)</td>
              {COLS.map(([k, , money]) => <td key={k} className="px-3 py-2.5 text-right tnum" data-testid={`zr-total-${k}`}>{money ? fmtMoney(data.totals[k]) : data.totals[k]}</td>)}
            </tr></tfoot>
          )}
        </table>
      </div>
      <Drill drill={drill} onClose={() => setDrill(null)} />
    </div>
  );
}
